import omit from 'lodash/omit';

import { mapSectorsIndustries, addSectorIndustryToData } from '../helpers';

import { IArticle, IArticleToDisplay } from '@/condor/domain/vo/Finsight/CreditFlowResearch/IArticle';
import ISector from '@/creditflowresearch/domain/vo/IDealSector';
import IIndustry from '@/creditflowresearch/domain/vo/IIndustry';
import Product, { AbsProduct, HycProduct, IgcProduct, ProductAbbreviations } from '@/creditflowresearch/domain/Product';
import { ITheme } from '@/condor/domain/vo/Finsight/CreditFlowResearch/ITheme';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import CorporateProduct from '@/creditflowresearch/domain/Product/entities/CorporateProduct';
import { EmergingMarket } from '@/creditflowresearch/domain/Market';

export const mapArticleToDisplay = (
  article: IArticle,
  themesCollection: ITheme[],
  products: IProduct[],
  regions: IRegion[],
  sectors: ISector[],
  industries: IIndustry[],
  productType: string | undefined,
) => {
  const mappedItem: IArticleToDisplay = {
    ...omit(article, ['sectors', 'industries']),
    products: [],
    regions: [],
    sectorOrIndustry: null,
    subsectorOrSubindustry: null,
  };

  const {
    themes,
    productsList,
    regionsList,
    marketIds,
  } = mapThemesProductsRegions(article, themesCollection, products, regions);
  mappedItem.themes = themes;
  mappedItem.products = productsList;
  mappedItem.regions = regionsList;

  const isAbsOnlyArticle = productsList.includes(AbsProduct.ABBREVIATION) && productsList.length === 1;
  const isCorporatesIncluded = productsList.some(
    (product: ProductAbbreviations) => [HycProduct.ABBREVIATION, IgcProduct.ABBREVIATION].includes(product),
  ) || marketIds.some((marketId) => [EmergingMarket.MARKET_ID].includes(marketId));

  const lowercasedProductType = productType?.toLowerCase() || Product.getDefaultAbbreviation().toLowerCase();
  const isAbsProductTab = lowercasedProductType === AbsProduct.ABBREVIATION.toLowerCase();
  const isCorporateTab = lowercasedProductType === CorporateProduct.ABBREVIATION.toLowerCase()
    || lowercasedProductType === HycProduct.ABBREVIATION.toLowerCase()
    || lowercasedProductType === IgcProduct.ABBREVIATION.toLowerCase()
    || lowercasedProductType === EmergingMarket.ABBREVIATION.toLowerCase();
  const isAllProductTab = lowercasedProductType === 'all';

  const showSector = isAbsProductTab || (isAllProductTab && isAbsOnlyArticle);
  const showIndustry = isCorporateTab || (isAllProductTab && isCorporatesIncluded);

  const {
    sectorsIndustries,
    subsectorsSubindustries,
  } = mapSectorsIndustries(
    article,
    sectors,
    industries,
    showSector,
    showIndustry,
  );
  return addSectorIndustryToData(mappedItem, sectorsIndustries, subsectorsSubindustries);
};

const mapThemesProductsRegions = (
  article: IArticle,
  themesCollection: ITheme[],
  products: IProduct[],
  regions: IRegion[],
) => {
  const regionAbbreviations = new Set<string>();
  const productAbbreviations = new Set<string>();
  const marketIds = new Set<string>();

  const themes = article.themes.map((themeId: string) => {
    const currentTheme = themesCollection.find(({ id }) => id === themeId);

    if (!currentTheme) return '';

    const themeProduct = products.find((product) => product.id === currentTheme.productId);

    if (themeProduct) {
      productAbbreviations.add(themeProduct.abbreviation);
    }

    const themeRegion = regions.find((region) => region.id === currentTheme.regionId);

    if (themeRegion) {
      regionAbbreviations.add(themeRegion.abbreviation);
    }

    if (currentTheme.marketId) {
      marketIds.add(currentTheme.marketId);
    }

    return currentTheme.themeName;
  }).filter(Boolean);

  return {
    themes,
    productsList: Array.from(productAbbreviations),
    regionsList: Array.from(regionAbbreviations),
    marketIds: Array.from(marketIds),
  };
};
